/**
 * registerBodyClipOnRig — hot-register an uploaded body clip on the SPLAT part rig.
 *
 * `window.__REGISTER_CUSTOM_BODY_CLIP__` is AvatarModel's seam, so it exists only for a
 * GLB avatar. On a splat character the body is the part rig (liveBodyRig.js), that global
 * is absent, and the upload path warned once and gave up — so a clip the user had just
 * saved did not exist on the rig until the page was reloaded and MultipartOGSModel
 * fetched every animation row again. This is the same registration for the other rig.
 *
 * `additive: false` in the rig's own vocabulary means "this clip is a full pose", NOT
 * "never layer it": the part rig bakes its own additive clone at play time
 * (PartRigDrive._additiveAction), and `additiveInPlace` is reserved for a clip that
 * ARRIVED as deltas — a kimodo gesture. Passing an upload's `additive` flag through
 * would tell the rig the pose is already a delta and skip the bake.
 */
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { getLiveBodyRig } from './liveBodyRig.js'
import { isTracksJsonPayload } from './bodyClipPayload.js'
import { buildGeneratedGestureClip } from './generatedGestureClip.js'

/**
 * Register `arrayBuffer` on the live body rig under `name` (and each alias).
 * @param {string} name primary runtime clip name
 * @param {ArrayBuffer} arrayBuffer a body GLB, or a stored kimodo tracks payload
 * @param {string[]} [aliases] extra keys naming the same clip (`custom_body_<id>`)
 * @returns {Promise<boolean>} false when there is no such rig — the caller's own
 *   "not ready" report is the loud one, and it names what is actually missing.
 */
export async function registerBodyClipOnLiveRig(name, arrayBuffer, aliases = []) {
  const rig = getLiveBodyRig()
  if (!rig?.register) return false

  // A gesture stored as kimodo tracks is not a GLB — build it the way the live
  // generation path does, against this rig's own rest pose, and say so.
  if (isTracksJsonPayload(arrayBuffer)) {
    const tracks = JSON.parse(new TextDecoder().decode(arrayBuffer))
    for (const key of clipKeys(name, aliases)) {
      rig.register(key, buildGeneratedGestureClip(tracks, { scene: rig.scene, name: key, restPose: rig.restPose }),
        { additive: true }) // already deltas — the rig must not bake it a second time
    }
    return true
  }

  const gltf = await new Promise((resolve, reject) => new GLTFLoader().parse(arrayBuffer, '', resolve, reject))
  const source = gltf.animations?.[0]
  if (!source) throw new Error(`registerBodyClipOnLiveRig: "${name}" carries no animation clip`)
  // Provenance in scene extras is what gates the pelvis .position (travel) prune, exactly
  // as on the GLB path — the rig reads it off the CLIP, so it has to be carried across.
  const marker = gltf.scene?.userData?.aosGeneratedMotion ?? null
  for (const key of clipKeys(name, aliases)) {
    // One clip object per key: the rig caches an action per clip, so two keys sharing one
    // would be one action under two names (the alias trap AvatarModel's aliasBodyClip
    // documents). A clone is array copies.
    const clip = source.clone()
    clip.name = key
    if (marker) clip.userData = { ...clip.userData, aosGeneratedMotion: marker }
    // The marker is stated even when it is NULL. An asset's travel is carried by nothing
    // else, so its pelvis track must survive the prune — and after a reload
    // MultipartOGSModel binds the very same file that way. The rig's own fallback assumes
    // a live generation, whose travel is already on the avatar root, and would strip it.
    rig.register(key, clip, { additive: false, generatedMarker: marker })
  }
  return true
}

/** The primary name plus its aliases, de-duplicated and without empties. */
function clipKeys(name, aliases) {
  return [...new Set([name, ...(aliases ?? [])].filter(Boolean))]
}
